import { createAsyncThunk } from "@reduxjs/toolkit";
import xhr from "@infobis/api-module";
import { AxiosRequestConfig } from "axios";
import { RootThunkAPI } from "../store";

type Request<R> = () => Promise<R>

interface ServerResponse<T> {
    [x: string]: any;
    success: boolean,
    data: T
}

export const ReduxAsyncThunkTry = async <ResponseType, >(thunkAPI: any, req: Request<ResponseType>) => {
    try {
        return await req()
    } catch (e) {
        return thunkAPI.rejectWithValue(e)
    }
}

interface ReduxRequestOptions {
    processException?: (e: Error) => Error
}

export const reduxRequest = async <ResponseType, >(req: Request<ResponseType>, options: ReduxRequestOptions = {}) : Promise<ResponseType> => {
    try {
        const response = await req() as ResponseType
        if (typeof response !== 'object') {
            throw new Error('Invalid response body. Expected JSON object.')
        } else if (response) {
            return response
        } else {
            throw new Error('Invalid response ' + response)
        }
    } catch (e: any) {
        if (options.processException)
            throw options.processException(e)
        else {
            const message = `An error occurred while fetching API ` + (e?.message || e || '')
            throw new Error(message)
        }
    }
}


export const TryServerResponseRequest = async <ResponseType,>(req: Request<ServerResponse<ResponseType>>, thunkAPI: any) : Promise<ResponseType> => {
    return ReduxAsyncThunkTry(thunkAPI, async () => {
        const response = await reduxRequest(req)
        return response.data ?? response;
    })
}

type DashboardResponse<R, P> = (requestOptions: AxiosRequestConfig<P>) => Promise<ServerResponse<R>>
type ResponseTransformer<R, T> = (x: ServerResponse<R>) => Promise<ServerResponse<T>>
const identityTransformer = <R, T>(x: ServerResponse<R>) => {
    return Promise.resolve(x) as unknown as Promise<ServerResponse<T>>
}

interface CreateThunkOptions<R, Z, D> extends Omit<AxiosRequestConfig<D>, 'url'> {
    prefix?: string
    urlPathParams?: Record<string, string>
    transformer?: (response: ServerResponse<R>) => Promise<ServerResponse<Z>>
    makeBeforeRequest?: (thunkAPI: RootThunkAPI) => any
}

export const Methods = {
    GET: 'GET',
    POST: 'POST',
    PUT: 'PUT',
    DELETE: 'DELETE',
} as const

export type MethodsType = typeof Methods[keyof typeof Methods]


export function dashboardFetchReq<R, P extends Record<string, any> = {}, T = R>(
    url: string,
    transformer: ResponseTransformer<R, T> = identityTransformer
): DashboardResponse<T, P> {
    return async (requestOptions: AxiosRequestConfig<P>) => {

        const {params, data, headers, baseURL, method = Methods.GET} = requestOptions;

        const request = {
            headers,
            params,
            method,
            data,
            baseURL,
            authorize: true,
        };
        const response = await xhr.fetch<ServerResponse<R>>(url, request);
        return transformer(response);
    }
}

export const createThunk =
    <R, P extends Record<string, any> = {}, D = any>(
        url: string,
        baseURL?: string,
        method: MethodsType | undefined = Methods.GET,
    ) => <T = R>(options: CreateThunkOptions<R, T, D>) => {
        const asyncThunk = createAsyncThunk<T, P, { rejectValue: { message: string } }>(
            options.prefix || url,
            async (payload: P, thunkAPI) => {

                const matchUrl = () => {
                    const urlPathParams = payload.urlPathParams as Record<string, string>;
                    return Object.entries(urlPathParams!).reduce((acc, [pathParameterKey, pathParameterValue]) => {
                        return acc.replaceAll(`:${pathParameterKey}`, pathParameterValue)
                    }, url);
                };

                const matchedUrl = !payload.urlPathParams ? url : matchUrl();

                const additionalPayload = options.makeBeforeRequest?.(thunkAPI as unknown as RootThunkAPI) || {};


                const makeRequest = () => {
                    return dashboardFetchReq<R, P, T>(matchedUrl, options.transformer)({
                        params: { ...payload.params, ...options.params, ...additionalPayload.params },
                        data: { ...payload.data, ...options.data, ...additionalPayload.data },
                        headers: { ...payload.headers, ...options.headers, ...additionalPayload.headers },
                        baseURL,
                        method,
                    })
                }
                return TryServerResponseRequest(makeRequest, thunkAPI);
            }
        );

        return asyncThunk;
    }

// export const createThunkWithoutRequest = <R, P extends Record<string, any> = {}>() => {
//     return () => createAsyncThunk<R, P, {rejectValue: {message: string}}>(options.prefix)
// }